import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./testing_tool.css";
import "./testing_tool_results.css";
import TestingToolIssueAccordion from "./testing_tool_issue_accordion";
import { exportTestingToolReport } from "./testing_tool_api_service";

const SEVERITY_OPTIONS = ["All", "Low", "Medium", "High"];
const SEGMENT_OPTIONS = ["All", "High", "Medium", "Low"];

function getEffortLevel(issue) {
  if (!issue) return "";
  if (typeof issue.effort === "string") return issue.effort;
  if (issue.effort && typeof issue.effort === "object") {
    return issue.effort.level || "";
  }
  if (typeof issue.effort_level === "string") return issue.effort_level;
  return "";
}

function normalizeSeverity(value) {
  const severity = String(value || "").toLowerCase();
  if (severity === "high" || severity === "critical" || severity === "major") {
    return "High";
  }
  if (severity === "medium" || severity === "moderate") {
    return "Medium";
  }
  if (severity === "low" || severity === "minor") {
    return "Low";
  }
  return "Medium";
}

function normalizeIssue(issue, index) {
  let card = {};
  if (issue?.issue_card) {
    if (typeof issue.issue_card === "object" && issue.issue_card !== null) {
      card = issue.issue_card;
    } else if (typeof issue.issue_card === "string") {
      try {
        card = JSON.parse(issue.issue_card);
      } catch (_) {}
    }
  }
  const merged = { ...card, ...issue };

  const sectionLabels = Array.isArray(merged.section_labels)
    ? merged.section_labels
    : merged.section_labels
      ? [merged.section_labels]
      : [];
  const pageType = Array.isArray(merged.page_type)
    ? merged.page_type
    : merged.page_type
      ? [merged.page_type]
      : [];

  const category =
    merged.category ||
    sectionLabels[0] ||
    pageType[0] ||
    merged.found_on ||
    merged.foundOn ||
    merged.domain ||
    "General";

  const issueText =
    merged.issue ||
    merged.user_problem ||
    merged.site_specific_description ||
    "";

  const potentialImpact =
    merged.potentialImpact ||
    merged.potential_impact ||
    merged.impact_rationale ||
    merged.dot_criterion ||
    "";

  const suggestedSolution =
    merged.suggestedSolution ||
    merged.suggested_solution ||
    merged.recommendation ||
    merged.site_specific_recommendation ||
    "";

  const foundOn =
    merged.found_on || merged.foundOn || merged.location || "";

  const evidence =
    merged.evidence ||
    merged.related_content ||
    merged.relatedContent ||
    "";

  return {
    ...merged,
    id: merged.id || merged.issue_id || `issue-${index}`,
    issue_id: merged.issue_id || merged.id || `issue-${index}`,
    category,
    title: merged.title || merged.issue_title || "Untitled issue",
    issue: issueText,
    user_problem: issueText,
    potentialImpact,
    suggestedSolution,
    recommendation: suggestedSolution,
    severity: normalizeSeverity(merged.severity),
    effort: merged.effort ?? merged.effort_level ?? "low",
    effort_level:
      typeof merged.effort_level === "string"
        ? merged.effort_level
        : merged.effort?.level ?? "low",
    estimated_fix_hours: merged.estimated_fix_hours,
    resolved: Boolean(merged.resolved),
    signals_to_detect: Array.isArray(merged.signals_to_detect)
      ? merged.signals_to_detect
      : [],
    page_type: pageType,
    section_labels: sectionLabels,
    evidence,
    source: merged.source,
    found_on: foundOn,
    page_url: merged.page_url || merged.pageUrl || "",
    confidence: merged.confidence ?? merged.confidence_weight,
    is_validated: merged.is_validated,
    imageConfirmed: merged.imageConfirmed ?? merged.image_confirmed,
  };
}

export default function TestingToolResultsView(testingToolResultsProps) {
  const {
    siteType,
    message,
    retrievedIssues,
    totalOpenFindings,
    totalResolvedFindings,
    totalEstimatedFixHours,
    testTestingToolId,
    onReaudit,
    onBackToProjects,
    isReauditing,
  } = testingToolResultsProps;

  const toolbarRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState("All categories");
  const [selectedSeverity, setSelectedSeverity] = useState("All");
  const [selectedSegment, setSelectedSegment] = useState("All");
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isSeverityOpen, setIsSeverityOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [issues, setIssues] = useState(
    Array.isArray(retrievedIssues) ? retrievedIssues : [],
  );

  useEffect(() => {
    setIssues(Array.isArray(retrievedIssues) ? retrievedIssues : []);
  }, [retrievedIssues]);

  const normalizedIssues = useMemo(
    () => issues.map((issue, index) => normalizeIssue(issue, index)),
    [issues],
  );

  const categories = useMemo(() => {
    const unique = Array.from(
      new Set(normalizedIssues.map((item) => item.category)),
    );
    return ["All categories", ...unique];
  }, [normalizedIssues]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!toolbarRef.current?.contains(event.target)) {
        setIsCategoryOpen(false);
        setIsSeverityOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => {
    if (!isReviewModalOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    const originalTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.touchAction = originalTouchAction;
    };
  }, [isReviewModalOpen]);

  const filteredIssues = normalizedIssues.filter((item) => {
    const byCategory =
      selectedCategory === "All categories" ||
      item.category === selectedCategory;

    const bySearch =
      !searchTerm.trim() ||
      [
        item.title,
        item.issue,
        item.user_problem,
        item.potentialImpact,
        item.suggestedSolution,
        item.recommendation,
        item.site_specific_description,
        item.site_specific_recommendation,
        item.impact_rationale,
        item.category,
        item.found_on,
        item.evidence,
        item.issue_id,
        ...(item.signals_to_detect || []),
        ...(item.section_labels || []),
        ...(item.page_type || []),
      ]
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.trim().toLowerCase());

    const bySegment =
      selectedSegment === "All" ||
      getEffortLevel(item).toLowerCase() === selectedSegment.toLowerCase();

    const byDropdownSeverity =
      selectedSeverity === "All" ||
      item.severity.toLowerCase() === selectedSeverity.toLowerCase();

    return byCategory && bySearch && bySegment && byDropdownSeverity;
  });

  const issuesByCategory = filteredIssues.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});
  const hasIssuesByCategory = Object.keys(issuesByCategory).length > 0;

  const totalIssuesCount = normalizedIssues.length;
  const resolvedIssuesCount = normalizedIssues.filter(
    (item) => item.resolved,
  ).length;
  const resolvedRatio =
    totalIssuesCount > 0 ? (resolvedIssuesCount / totalIssuesCount) * 100 : 0;
  const safeTotalFindings =
    typeof totalOpenFindings === "number" && totalOpenFindings > 0
      ? totalOpenFindings
      : totalIssuesCount;
  const safeResolvedFindings =
    typeof totalResolvedFindings === "number"
      ? totalResolvedFindings
      : resolvedIssuesCount;
  const summaryResolvedPercent =
    safeTotalFindings > 0
      ? (safeResolvedFindings / safeTotalFindings) * 100
      : 0;
  const severityCounts = useMemo(
    () =>
      normalizedIssues.reduce(
        (acc, issue) => {
          const key = issue.severity.toLowerCase();
          if (key === "high" || key === "medium" || key === "low") {
            acc[key] += 1;
          }
          return acc;
        },
        { high: 0, medium: 0, low: 0 },
      ),
    [normalizedIssues],
  );
  const majorCategorySummary = useMemo(() => {
    const rankedCategories = Object.entries(
      normalizedIssues.reduce((acc, issue) => {
        acc[issue.category] = (acc[issue.category] || 0) + 1;
        return acc;
      }, {}),
    )
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([category]) => category);

    if (rankedCategories.length === 0) return "No dominant category yet.";
    if (rankedCategories.length === 1) return rankedCategories[0];
    return `${rankedCategories[0]} and ${rankedCategories[1]}`;
  }, [normalizedIssues]);
  const priorityActionText =
    severityCounts.high > 0
      ? "Start with high-severity blockers, then tackle medium-impact flow fixes, and finish with low-severity polish."
      : severityCounts.medium > 0
      ? "Prioritize medium-severity flow and clarity issues first, then close low-severity improvements."
      : "Focus on low-severity refinements to improve overall usability and consistency.";

  function handleToggleResolved(issueId) {
    setIssues((prev) =>
      prev.map((item) =>
        item.id === issueId || item.issue_id === issueId
          ? { ...item, resolved: !item.resolved }
          : item,
      ),
    );
  }

  async function handleExport() {
    if (!testTestingToolId || isExporting || isReauditing) return;

    try {
      setIsExporting(true);
      const data = await exportTestingToolReport(testTestingToolId);
      const fileUrl = data?.file_url || data?.url;
      if (!fileUrl) {
        throw new Error("Export file URL is missing");
      }
      const link = document.createElement("a");
      link.href = fileUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Failed to export testing tool report", error);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="testing_tool">
      <header className="testing_tool-header">
        <div className="testing_tool-header-inner testing_tool-header-inner--results">
          <Link to="/testing-tool" className="testing_tool-logo">
            <img src="/img/testing-tool/UserQ-logo.png" alt="UserQ" />
          </Link>
        </div>
      </header>
      <div className="testing-tool-results-body">
        <div className="testing-tool-results-top">
          <div>
            <p className="testing-tool-results-breadcrumb">
              <button
                type="button"
                className="testing-tool-results-breadcrumb-link"
                onClick={onBackToProjects}
              >
                Projects
              </button>
              <img src="/img/testing-tool/Vector.svg" alt="nagivation arrow" />
              <span>{siteType} website</span>
            </p>
            <h2 className="testing-tool-results-title">
              {siteType} website results
            </h2>
            <p className="testing-tool-results-subtitle">{message}</p>
          </div>
          <div className="testing-tool-results-actions">
            <button
              type="button"
              className="testing-tool-results-btn"
              onClick={handleExport}
              disabled={isReauditing || isExporting || !testTestingToolId}
            >
              <img src="/img/testing-tool/export.svg" alt="export icon" />
              {isExporting ? "Exporting..." : "Export"}
            </button>
            <button
              type="button"
              className={`testing-tool-results-btn testing-tool-results-btn--dark${
                isReauditing ? " is-loading" : ""
              }`}
              onClick={onReaudit}
              disabled={isReauditing}
            >
              {isReauditing ? (
                <img
                  src="/img/testing-tool/SpinnerGap.svg"
                  alt=""
                  aria-hidden
                  className="testing-tool-results-btn-spinner"
                />
              ) : (
                <img src="/img/testing-tool/Re-audit.svg" alt="re-audit icon" />
              )}
              {isReauditing ? "Re-auditing..." : "Re-audit"}
            </button>
          </div>
        </div>

        <div
          className={`testing-tool-results-cards${isReauditing ? " is-disabled" : ""}`}
          aria-disabled={isReauditing}
        >
          <article className="testing-tool-results-card">
            <div className="testing-tool-results-card-head">
              <span className="testing-tool-results-icon">
                <img
                  src="/img/testing-tool/AuditSummary.svg"
                  alt="Audit Summary"
                />
              </span>
              <p className="testing-tool-results-card-title">Audit summary</p>
            </div>
            <p className="testing-tool-results-card-value">
              {safeResolvedFindings}
              <span>/{safeTotalFindings}</span>
            </p>
            <div className="testing-tool-results-card-row">
              <p>Open findings</p>
              <span>{Math.round(summaryResolvedPercent)}% resolved</span>
            </div>
            <div className="testing-tool-results-progress">
              <span
                style={{
                  width: `${Math.min(Math.max(summaryResolvedPercent, 0), 100)}%`,
                }}
              />
            </div>
          </article>

          <article className="testing-tool-results-card">
            <div className="testing-tool-results-card-head">
              <span className="testing-tool-results-icon">
                <img
                  src="/img/testing-tool/Criticalissues.svg"
                  alt="Critical issues"
                />
              </span>
              <p className="testing-tool-results-card-title">Critical issues</p>
            </div>
            <p className="testing-tool-results-card-value">
              {retrievedIssues.length}
            </p>
            <p className="testing-tool-results-card-row">
              High severity needs attention
            </p>
          </article>

          <article className="testing-tool-results-card">
            <div className="testing-tool-results-card-head">
              <span className="testing-tool-results-icon">
                <img src="/img/testing-tool/Esteffort.svg" alt="Est. effort" />
              </span>
              <p className="testing-tool-results-card-title">Est. effort</p>
            </div>
            <p className="testing-tool-results-card-value">
              {totalEstimatedFixHours}
            </p>
            <p className="testing-tool-results-card-row">
              Total implementation time
            </p>
          </article>
        </div>

        <div
          className={`testing-tool-results-filter${isReauditing ? " is-disabled" : ""}`}
          ref={toolbarRef}
          aria-disabled={isReauditing}
        >
          <div className="testing-tool-results-search-wrap">
            <img
              src="/img/testing-tool/search.svg"
              alt=""
              aria-hidden
              className="testing-tool-results-search-icon"
            />
            <input
              type="text"
              className="testing-tool-results-search"
              placeholder="Search issues, categories, or keywords..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <div className="testing-tool-results_wrapper">
            <div className="testing-tool-results-severity">
              {SEGMENT_OPTIONS.map((segment) => (
                <button
                  key={segment}
                  type="button"
                  className={selectedSegment === segment ? "is-active" : ""}
                  onClick={() => setSelectedSegment(segment)}
                >
                  {segment}
                </button>
              ))}
            </div>
            <div className="testing-tool-results-divider" />
            <div className="testing-tool-results-dropdown-wrap">
              <button
                type="button"
                className={`testing-tool-results-category${isCategoryOpen ? " is-open" : ""}`}
                onClick={() => {
                  setIsCategoryOpen((open) => !open);
                  setIsSeverityOpen(false);
                }}
              >
                {selectedCategory}
                <img
                  src="/img/testing-tool/arrow-down-round.svg"
                  alt=""
                  aria-hidden
                  className="testing-tool-results-arrow"
                />
              </button>
              {isCategoryOpen ? (
                <div className="testing-tool-results-menu testing-tool-results-menu--category">
                  {categories.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`testing-tool-results-menu-item${selectedCategory === option ? " is-selected" : ""}`}
                      onClick={() => {
                        setSelectedCategory(option);
                        setIsCategoryOpen(false);
                      }}
                    >
                      <span>{option}</span>
                      {selectedCategory === option ? (
                        <span className="testing-tool-results-check" aria-hidden>
                          <img
                            src="/img/testing-tool/filterTick.svg"
                            alt="filterTick"
                          />
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="testing-tool-results-dropdown-wrap">
              <button
                type="button"
                className={`testing-tool-results-view${isSeverityOpen ? " is-open" : ""}`}
                onClick={() => {
                  setIsSeverityOpen((open) => !open);
                  setIsCategoryOpen(false);
                }}
              >
                <img src="/img/testing-tool/sorting.svg" alt="Shorting" />
              </button>
              {isSeverityOpen ? (
                <div className="testing-tool-results-menu testing-tool-results-menu--severity">
                  {SEVERITY_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`testing-tool-results-menu-item${selectedSeverity === option ? " is-selected" : ""}`}
                      onClick={() => {
                        setSelectedSeverity(option);
                        setIsSeverityOpen(false);
                      }}
                    >
                      <span>{option}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div
          className={`testing-tool-results-findings${isReauditing ? " is-disabled" : ""}`}
          aria-disabled={isReauditing}
        >
          <div className="testing-tool-results-issues-summary">
            <p className="testing-tool-results-issues-summary-title">
              Issues overview
            </p>
            <p className="testing-tool-results-issues-summary-copy">
              Major issues are concentrated in {majorCategorySummary}, with{" "}
              {severityCounts.high} high, {severityCounts.medium} medium, and{" "}
              {severityCounts.low} low severity findings. Overall UX shows{" "}
              {severityCounts.high > 0
                ? "critical friction in key journeys"
                : severityCounts.medium > 0
                ? "moderate friction across important flows"
                : "minor friction that mostly affects polish"}
              . {priorityActionText}
            </p>
          </div>
          {hasIssuesByCategory ? (
            Object.entries(issuesByCategory).map(([category, categoryIssues]) => (
              <TestingToolIssueAccordion
                key={category}
                sectionLabel={category}
                items={categoryIssues}
                onToggleResolved={handleToggleResolved}
              />
            ))
          ) : (
            <p className="testing-tool-results-empty-message">
              No issues found for the selected filters.
            </p>
          )}
        </div>

        <div
          className="testing-tool-results-fixed-strip"
          role="region"
          aria-label="Review progress"
        >
          <div className="testing-tool-results-fixed-progress">
            <img src="/img/testing-tool/issuesresolved.svg" alt="issuesresolved" />
            <p>
              {resolvedIssuesCount} of {totalIssuesCount} issues resolved
            </p>
            <div className="testing-tool-results-fixed-progressbar" aria-hidden>
              <span style={{ width: `${resolvedRatio}%` }} />
            </div>
          </div>
          <button
            type="button"
            className="testing-tool-results-fixed-cta"
            onClick={() => setIsReviewModalOpen(true)}
            disabled={isReauditing}
          >
            Complete review
          </button>
        </div>

        {isReviewModalOpen ? (
          <div
            className="testing-tool-results-modal-backdrop"
            role="dialog"
            aria-modal="true"
          >
            <div className="testing-tool-results-modal">
              <div className="testing-tool-results-modal-icon" aria-hidden>
                <img src="/img/testing-tool/reviewIcon.svg" alt="reviewIcon" />
              </div>
              <h3>Review completed!</h3>
              <p>
                You&apos;ve resolved {resolvedIssuesCount} out of{" "}
                {totalIssuesCount} findings. Great progress on improving the user
                experience!
              </p>
              <div className="testing-tool-results-modal-stats">
                <div className="testing-tool-results-modal-stat-card">
                  <strong>{resolvedIssuesCount}</strong>
                  <span>FIXED</span>
                </div>
                <div className="testing-tool-results-modal-stat-card">
                  <strong>
                    {Math.max(totalIssuesCount - resolvedIssuesCount, 0)}
                  </strong>
                  <span>REMAINING</span>
                </div>
              </div>
              <div className="testing-tool-results-modal-actions">
                <button
                  type="button"
                  className="testing-tool-results-modal-btn testing-tool-results-modal-btn--dark"
                  onClick={() => {
                    window.location.href = "/testing-tool";
                  }}
                >
                  Back to Projects
                </button>
                <button
                  type="button"
                  className="testing-tool-results-modal-btn"
                  onClick={() => setIsReviewModalOpen(false)}
                >
                  Continue reviewing
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
